import styles from "./ShortForm.module.scss"

import React, { useMemo, useState } from "react"

import { ShortsListItem } from "@shared/apis/shorts"
import { TextWithTooltip } from "@shared/components"
import { useAccount } from "@shared/contexts"
import { isEmptyStr } from "@shared/modules"

import dayjs from "dayjs"

import MinusIcon from "./icons/MinusIcon"
import PlusIcon from "./icons/PlusIcon"

const MIN_FONT = 12
const MAX_FONT = 28
const DEFAULT_FONT = 16
const STEP = 2
const WATERMARK_REPEAT = 400

// 요약 본문은 복사하지 못하게 막는다.
const preventDefault = (e: React.SyntheticEvent) => e.preventDefault()

export type AiSummaryItem = Pick<
  ShortsListItem,
  "title" | "createdAt" | "finalSummary"
>

interface Props {
  item: AiSummaryItem
  streamerNickname?: string
  onClose: () => void
}

export default function AiSummaryPopup({
  item,
  streamerNickname,
  onClose,
}: Props) {
  const { account } = useAccount()
  const [fontSize, setFontSize] = useState(DEFAULT_FONT)

  const isEmptySummary = isEmptyStr(item.finalSummary)

  const nickname = account?.viewer?.nickname ?? ""
  const watermark = useMemo(
    () => (nickname ? `${nickname} `.repeat(WATERMARK_REPEAT) : ""),
    [nickname],
  )

  return (
    <div className={styles.popupOverlay} onClick={onClose}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <div className={styles.popupHeader}>
          <span className={styles.popupTitle}>AI 요약</span>
          <button
            type="button"
            className={styles.popupClose}
            title="닫기"
            onClick={onClose}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M2 2 L12 12 M12 2 L2 12"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className={styles.infoHeader}>
          <TextWithTooltip text={item.title ?? ""} className={styles.title} />
          <div className={styles.metaRow}>
            <div className={styles.metaInfo}>
              <span className={styles.author}>{streamerNickname}</span>
              <span className={styles.divider} />
              <span className={styles.date}>
                {item.createdAt
                  ? dayjs(item.createdAt).format("YYYY-MM-DD")
                  : "-"}
              </span>
            </div>
            <div className={styles.fontControls}>
              <button
                type="button"
                className={styles.fontButton}
                disabled={isEmptySummary || fontSize >= MAX_FONT}
                onClick={() =>
                  setFontSize((size) => Math.min(MAX_FONT, size + STEP))
                }
              >
                <PlusIcon />
                크게
              </button>
              <button
                type="button"
                className={styles.fontButton}
                disabled={isEmptySummary || fontSize <= MIN_FONT}
                onClick={() =>
                  setFontSize((size) => Math.max(MIN_FONT, size - STEP))
                }
              >
                <MinusIcon />
                작게
              </button>
            </div>
          </div>
        </div>

        <div className={styles.popupBody}>
          <div
            className={styles.bodyBox}
            onCopy={preventDefault}
            onCut={preventDefault}
            onContextMenu={preventDefault}
            onDragStart={preventDefault}
          >
            {watermark && (
              <div className={styles.watermark} aria-hidden="true">
                {watermark}
              </div>
            )}
            {!isEmptySummary ? (
              <div className={styles.bodyContent} style={{ fontSize }}>
                {item.finalSummary}
              </div>
            ) : (
              <div className={styles.emptySummary}>
                요약본을 불러오지 못했습니다.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
